import {
  avisoDeCvRechazado,
  errorDelCvAlEnviar,
  type CvRechazado,
} from '@/features/rh/lib/cvDelCandidato';
import React, { useState, useEffect } from 'react';
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton';
import { Checkbox } from '@/shared/components/Checkbox'
import SelectBuscable from '@/shared/components/SelectBuscable'
import { useSearchParams } from 'react-router-dom';
import { Upload, Check, AlertCircle, Building2, X, FileText } from 'lucide-react';
import type { CandidateRegistration, CandidateFormErrors } from '@/types/candidate';
import { candidateService } from '@/features/rh/api/candidateService';
import {
  MAX_CIUDAD,
  errorDeCiudad,
  errorDeSueldoDeseado,
  mensajeNativoDelSueldo,
  sueldoDesdeInput,
} from '@/features/rh/lib/postulacionPublica';
import { origenesDeCandidato } from '@/features/rh/api/origenesDeCandidato';
import { estadosYCiudades } from '@/features/rh/api/estadosYCiudades';
import {
  errorDeResidencia,
  textoDeResidencia,
  type CiudadDeMexico,
  type EstadoDeMexico,
} from '@/features/rh/lib/ciudadDeResidencia';
import {
  pideQuienRecomendo,
  type OrigenDeCandidato,
} from '@/features/rh/lib/origenDelCandidato';
import { s3Service } from '@/services/s3Service';
import logoBig from '@/assets/big-logo.svg';
import AvisoDePrivacidadSimplificado, {
  RUTA_AVISO_INTEGRAL,
} from '@/features/rh/components/AvisoDePrivacidadSimplificado';

interface JobRequestInfo {
  id: number;
  areaVacante: string;
  sede: string;
  puestoSolicitar: string;
  jornada: string;
  prioridad: string;
  justificacionNecesidad: string;
  numeroPlazas: number;
  fechaRequeridaInicio: string;
  habilidadesMinimas: string;
  prestaciones: string;
  motivo: string;
  tipoPerfil: string;
  otraInformacion: string;
  fechaCreacion: string;
}

const CandidateRegistrationForm: React.FC = () => {
  const [searchParams] = useSearchParams();
  
  // Extraer token seguro desde URL
  const publicToken = searchParams.get('token') || undefined;
  const fallbackJobRequestId = searchParams.get('jobRequestId') || undefined;
  
  const [jobInfo, setJobInfo] = useState<JobRequestInfo | null>(null);
  const [isLoadingJobInfo, setIsLoadingJobInfo] = useState(false);
  const [formData, setFormData] = useState<CandidateRegistration>({
    fullName: '',
    phone: '',
    email: '',
    age: undefined,
    profesion: '',
    experienceYears: undefined,
    certificaciones: '',
    residenceCity: '',
    experienceDescription: '',
    cv: undefined,
    privacyConsent: false,
    jobRequestId: '', // Se setea después de resolver token seguro
  });
  
  const [origenes, setOrigenes] = useState<OrigenDeCandidato[]>([]);

  /**
   * Solo los origenes marcados como publicos (RH-06). Si el catalogo no
   * responde el campo no aparece y la postulacion sigue: es opcional, y
   * perder un candidato por un desplegable seria el peor intercambio.
   */
  useEffect(() => {
    let vigente = true;
    origenesDeCandidato
      .publicos()
      .then((lista) => vigente && setOrigenes(lista))
      .catch(() => undefined);
    return () => {
      vigente = false;
    };
  }, []);

  const pideRecomendador = pideQuienRecomendo(origenes, formData.sourceId);

  const [estados, setEstados] = useState<EstadoDeMexico[]>([]);
  const [estadoId, setEstadoId] = useState('');
  const [ciudades, setCiudades] = useState<CiudadDeMexico[]>([]);
  const [ciudadId, setCiudadId] = useState('');
  const [cargandoCiudades, setCargandoCiudades] = useState(false);
  // Si el catalogo no responde se vuelve al campo de texto de antes: perder una
  // postulacion por un desplegable que no carga seria el peor intercambio.
  const [catalogoNoDisponible, setCatalogoNoDisponible] = useState(false);

  useEffect(() => {
    let vigente = true;
    estadosYCiudades
      .estados()
      .then((lista) => vigente && setEstados(lista))
      .catch(() => vigente && setCatalogoNoDisponible(true));
    return () => {
      vigente = false;
    };
  }, []);

  const elegirEstado = (valor: string) => {
    setEstadoId(valor);
    setCiudadId('');
    setCiudades([]);
    setFormData((previos) => ({ ...previos, residenceCity: '' }));
    if (!valor) return;
    setCargandoCiudades(true);
    estadosYCiudades
      .ciudades(Number(valor))
      .then((lista) => setCiudades(lista))
      .catch(() => setCatalogoNoDisponible(true))
      .finally(() => setCargandoCiudades(false));
  };

  const elegirCiudad = (valor: string) => {
    setCiudadId(valor);
    const ciudad = ciudades.find((c) => String(c.id) === valor);
    const estado = estados.find((e) => String(e.id) === estadoId);
    setFormData((previos) => ({
      ...previos,
      residenceCity: ciudad && estado ? textoDeResidencia(ciudad.name, estado.name) : '',
    }));
    if (errors.residenceCity) {
      setErrors((previos) => ({ ...previos, residenceCity: undefined }));
    }
  };

  const usaCatalogoDeCiudades = estados.length > 0 && !catalogoNoDisponible;

  const [errors, setErrors] = useState<CandidateFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [isUploadingCV, setIsUploadingCV] = useState(false);
  const [cvFileName, setCvFileName] = useState<string>('');
  // El último archivo que se rechazó, para no olvidar el motivo al enviar (SRH-332).
  const [cvRechazado, setCvRechazado] = useState<CvRechazado | null>(null);

  // Cargar información de la vacante si viene por URL
  useEffect(() => {
    if (publicToken) {
      setIsLoadingJobInfo(true);
      candidateService.getJobRequestInfo(publicToken).then(info => {
        if (info) {
          setJobInfo(info);
          setFormData(prev => ({ ...prev, jobRequestId: String(info.id) }));
        }
      }).catch(error => {
        console.error('Error obteniendo información de la vacante:', error);
      }).finally(() => {
        setIsLoadingJobInfo(false);
      });
    } else if (fallbackJobRequestId) {
      setFormData(prev => ({ ...prev, jobRequestId: String(fallbackJobRequestId) }));
    }
  }, [publicToken, fallbackJobRequestId]);

  const validateForm = (): boolean => {
    const newErrors: CandidateFormErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'El nombre completo es requerido';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'El teléfono es requerido';
    } else if (!/^\d+$/.test(formData.phone)) {
      newErrors.phone = 'El teléfono solo debe contener dígitos';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'El email es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Formato de email no válido';
    }

    if (formData.age === undefined) {
      newErrors.age = 'La edad es requerida';
    } else if (formData.age < 16 || formData.age > 100) {
      newErrors.age = 'La edad debe estar entre 16 y 100 años';
    }

    if (!formData.profesion.trim()) {
      newErrors.profesion = 'La profesión es requerida';
    }

    if (formData.experienceYears !== undefined && (formData.experienceYears < 0 || formData.experienceYears > 50)) {
      newErrors.experienceYears = 'Los años de experiencia deben estar entre 0 y 50';
    }

    if ((formData.certificaciones || '').trim().length > 500) {
      newErrors.certificaciones = 'Las certificaciones no pueden exceder 500 caracteres';
    }

    const errorCiudad = usaCatalogoDeCiudades
      ? errorDeResidencia(estadoId, ciudadId)
      : errorDeCiudad(formData.residenceCity);
    if (errorCiudad) {
      newErrors.residenceCity = errorCiudad;
    }

    const errorSueldo = errorDeSueldoDeseado(formData.salaryExpectation);
    if (errorSueldo) {
      newErrors.salaryExpectation = errorSueldo;
    }

    if (!formData.experienceDescription.trim()) {
      newErrors.experienceDescription = 'La descripción de experiencia es requerida';
    } else if (formData.experienceDescription.trim().length < 10) {
      newErrors.experienceDescription = 'La descripción debe tener al menos 10 caracteres';
    }

    if (!formData.privacyConsent) {
      newErrors.privacyConsent = 'Debe aceptar el consentimiento de privacidad';
    }

    // El nombre de quien recomendo solo se exige si el origen elegido lo pide.
    if (pideRecomendador && !formData.referredByText?.trim()) {
      newErrors.referredByText = 'Indícanos quién te recomendó';
    }

    // Validar que existe un ID de solicitud válido
    if (!formData.jobRequestId || formData.jobRequestId.trim() === '') {
      newErrors.fullName = 'Link de vacante inválido. Por favor, use el enlace proporcionado por la empresa.';
    }

    // Validar CV solo si el tipo de perfil NO es "tecnico"
    const errorDelCv = errorDelCvAlEnviar({
      seExige: jobInfo?.tipoPerfil !== 'tecnico',
      tieneArchivo: Boolean(formData.cv || formData.cvUrl),
      rechazado: cvRechazado,
    });
    if (errorDelCv) {
      newErrors.cv = errorDelCv;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    setFormData(prev => ({
      ...prev,
      // Un campo numerico vaciado vuelve a indefinido y no a cero: con cero
      // la pantalla mostraria un 0 que la persona no escribio.
      [name]:
        type === 'number'
          ? value === ''
            ? undefined
            : Number.isNaN(parseInt(value))
              ? prev[name as keyof CandidateRegistration]
              : parseInt(value)
          : value,
    }));

    // Limpiar error cuando el usuario empiece a escribir
    if (errors[name as keyof CandidateFormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  /**
   * Traduce el aviso propio del navegador antes de que lo muestre.
   *
   * Se limpia primero para poder leer la validez real: con un customValidity
   * puesto, el campo siempre se reporta invalido y no se sabria por que.
   */
  const traduceElAvisoDelSueldo = (input: HTMLInputElement) => {
    input.setCustomValidity('');
    input.setCustomValidity(mensajeNativoDelSueldo(input.validity));
  };

  /** El sueldo no pasa por handleInputChange: ese usa parseInt. */
  const handleSalaryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    traduceElAvisoDelSueldo(e.target);

    setFormData(prev => ({
      ...prev,
      salaryExpectation: sueldoDesdeInput(value),
    }));

    if (errors.salaryExpectation) {
      setErrors(prev => ({ ...prev, salaryExpectation: undefined }));
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked,
    }));

    if (errors[name as keyof CandidateFormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    
    if (!file) {
      return;
    }

    // Validar el archivo
    const validation = s3Service.validateFile(file);
    if (!validation.valid) {
      const rechazo = {
        nombre: file.name,
        motivo: validation.error ?? 'El archivo no es válido',
      };
      setCvRechazado(rechazo);
      setErrors(prev => ({
        ...prev,
        cv: avisoDeCvRechazado(rechazo)
      }));
      // Se vacía el campo: sin esto, elegir de nuevo el mismo archivo no
      // dispara ningún cambio y parece que no pasa nada.
      e.target.value = '';
      return;
    }

    // Limpiar errores previos
    setCvRechazado(null);
    setErrors(prev => ({
      ...prev,
      cv: undefined
    }));

    // Subir a S3
    setIsUploadingCV(true);
    setCvFileName(file.name);

    try {
      const uploadResult = await s3Service.uploadFile(file, 'cvs');
      
      if (uploadResult.success && uploadResult.url) {
        // Guardar la URL en el formData
        setFormData(prev => ({
          ...prev,
          cv: file,
          cvUrl: uploadResult.url
        }));
      } else {
        const rechazo = {
          nombre: file.name,
          motivo: uploadResult.message || 'Error al subir el archivo',
        };
        setCvRechazado(rechazo);
        setErrors(prev => ({
          ...prev,
          cv: avisoDeCvRechazado(rechazo)
        }));
        setCvFileName('');
      }
    } catch {
      const rechazo = {
        nombre: file.name,
        motivo: 'Error al subir el archivo. Por favor, inténtalo de nuevo',
      };
      setCvRechazado(rechazo);
      setErrors(prev => ({
        ...prev,
        cv: avisoDeCvRechazado(rechazo)
      }));
      setCvFileName('');
    } finally {
      setIsUploadingCV(false);
    }
  };

  const handleRemoveCV = () => {
    setFormData(prev => ({
      ...prev,
      cv: undefined,
      cvUrl: undefined
    }));
    setCvFileName('');
    setCvRechazado(null);
    setErrors(prev => ({
      ...prev,
      cv: undefined
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await candidateService.submitCandidateRegistration(formData);
      
      if (result.success) {
        setIsSubmitted(true);
        setSubmitMessage('Tu solicitud ha sido enviada exitosamente');
      } else {
        setSubmitMessage(result.message || 'Error al enviar el registro');
      }
    } catch {
      setSubmitMessage('Error al enviar el registro. Por favor, inténtelo de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
        <div className="w-full max-w-none px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              ¡Registro Exitoso!
            </h1>
            <p className="text-lg text-gray-600 mb-6">
              {submitMessage}
            </p>
            <p className="text-gray-500">
              Nos pondremos en contacto contigo pronto para continuar con el proceso.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Mostrar error si no hay un ID de solicitud válido
  if (!publicToken && !fallbackJobRequestId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100 py-12 px-4">
        <div className="w-full max-w-none px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              Link Inválido
            </h1>
            <p className="text-gray-600 mb-6">
              No se encontró una vacante válida en este enlace. 
              Por favor, verifica que estés usando el enlace correcto proporcionado por la empresa.
            </p>
            <div className="bg-gray-50 rounded-lg p-4 text-left">
              <p className="text-sm text-gray-700">
                <strong>¿Necesitas ayuda?</strong><br/>
                Contacta con el equipo de recursos humanos que te proporcionó este enlace.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
      <div className="w-full max-w-none px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-6">
            <img 
              src={logoBig} 
              alt="SLYRSA Logo" 
              className="h-20 w-auto"
            />
          </div>
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">
            Registro de Candidato
          </h2>
          <p className="text-gray-600">
            Completa este formulario para postularte a nuestras oportunidades laborales. 
            Todos los campos marcados con * son obligatorios.
          </p>
        </div>

        <AvisoDePrivacidadSimplificado />

        {/* Job Info Card (if applying for specific job) */}
        {isLoadingJobInfo && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl shadow-lg p-6 mb-8 border-2 border-blue-200 animate-pulse">
            <div className="flex items-start">
              <div className="w-14 h-14 bg-blue-300 rounded-xl mr-4 flex-shrink-0"></div>
              <div className="flex-1 space-y-3">
                <div className="h-6 bg-blue-200 rounded w-3/4"></div>
                <div className="h-4 bg-blue-200 rounded w-1/2"></div>
                <div className="h-4 bg-blue-200 rounded w-full"></div>
              </div>
            </div>
          </div>
        )}
        
        {!isLoadingJobInfo && jobInfo && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl shadow-lg p-6 mb-8 border-2 border-blue-200">
            <div className="flex items-start">
              <div className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center mr-4 flex-shrink-0 shadow-md">
                <Building2 className="w-7 h-7 text-white" />
              </div>
              <div className="flex-1">
                <div className="inline-block bg-blue-600 text-white text-xs font-semibold px-3 py-1 rounded-full mb-2">
                  Te estás postulando a:
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  {jobInfo.puestoSolicitar}
                </h3>
                <div className="space-y-2">
                  <p className="text-lg text-gray-700 font-medium flex items-center">
                    <svg className="w-5 h-5 mr-2 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
                      <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z" />
                    </svg>
                    Departamento/Área: {jobInfo.areaVacante}
                  </p>
                  <p className="text-sm text-gray-600 flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                    Ubicación: {jobInfo.sede}
                  </p>
                  {jobInfo.habilidadesMinimas && (
                    <>
                      <p className="text-xs font-semibold text-gray-700 mb-1">
                        Habilidades requeridas:
                      </p>
                      <div className="mt-3 p-3 bg-white rounded-lg border border-blue-100">
                        <div
                          className="text-sm text-gray-600"
                          dangerouslySetInnerHTML={{
                            __html: jobInfo.habilidadesMinimas,
                          }}
                        />
                      </div>
                    </>
                  )}
                  {jobInfo.prestaciones && (
                    <>
                      <p className="text-xs font-semibold text-gray-700 mb-1">
                        Prestaciones:
                      </p>
                      <div className="mt-2 p-3 bg-white rounded-lg border border-blue-100">
                        <div
                          className="text-sm text-gray-600"
                          dangerouslySetInnerHTML={{
                            __html: jobInfo.prestaciones,
                          }}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Full Name */}
              <div className="md:col-span-2">
                <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre Completo *
                </label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                    errors.fullName ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="Tu nombre completo"
                />
                {errors.fullName && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.fullName}
                  </p>
                )}
              </div>

              {/* Phone */}
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Teléfono *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                    errors.phone ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="5512345678"
                />
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.phone}
                  </p>
                )}
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                    errors.email ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="tu@email.com"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.email}
                  </p>
                )}
              </div>

              {/* Age */}
              <div>
                <label htmlFor="age" className="block text-sm font-medium text-gray-700 mb-2">
                  Edad *
                </label>
                <input
                  type="number"
                  id="age"
                  name="age"
                  min="16"
                  max="100"
                  value={formData.age ?? ''}
                  onChange={handleInputChange}
                  placeholder="Tu edad"
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                    errors.age ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                />
                {errors.age && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.age}
                  </p>
                )}
              </div>

              {/* Profession */}
              <div>
                <label htmlFor="profesion" className="block text-sm font-medium text-gray-700 mb-2">
                  Profesión *
                </label>
                <input
                  type="text"
                  id="profesion"
                  name="profesion"
                  value={formData.profesion}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                    errors.profesion ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="Ej. Ingeniero Industrial"
                />
                {errors.profesion && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.profesion}
                  </p>
                )}
              </div>

              {/* Experience Years */}
              <div>
                <label htmlFor="experienceYears" className="block text-sm font-medium text-gray-700 mb-2">
                  Años de Experiencia *
                </label>
                <input
                  type="number"
                  id="experienceYears"
                  name="experienceYears"
                  min="0"
                  max="50"
                  value={formData.experienceYears}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                    errors.experienceYears ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                />
                {errors.experienceYears && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.experienceYears}
                  </p>
                )}
              </div>

              {/* Certificaciones */}
              <div>
                <label htmlFor="certificaciones" className="block text-sm font-medium text-gray-700 mb-2">
                  Certificaciones
                </label>
                <input
                  type="text"
                  id="certificaciones"
                  name="certificaciones"
                  value={formData.certificaciones}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                    errors.certificaciones ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="Ej. AWS, Scrum Master, PMP"
                />
                {errors.certificaciones && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.certificaciones}
                  </p>
                )}
              </div>

              {/* Estado y ciudad de residencia */}
              {usaCatalogoDeCiudades && (
                <div>
                  <label htmlFor="residenceState" className="block text-sm font-medium text-gray-700 mb-2">
                    Estado de Residencia *
                  </label>
                  <SelectBuscable
                    variante="formulario"
                    id="residenceState"
                    name="residenceState"
                    value={estadoId}
                    onChange={elegirEstado}
                    error={Boolean(errors.residenceCity) && !estadoId}
                    canDelete={false}
                    placeholder="Selecciona un estado"
                    placeholderDeBusqueda="Buscar estado..."
                    opciones={estados.map((e) => ({ value: String(e.id), label: e.name }))}
                  />
                </div>
              )}

              <div>
                <label htmlFor="residenceCity" className="block text-sm font-medium text-gray-700 mb-2">
                  Ciudad de Residencia *
                </label>
                {usaCatalogoDeCiudades ? (
                  <SelectBuscable
                    variante="formulario"
                    id="residenceCity"
                    name="residenceCity"
                    value={ciudadId}
                    onChange={elegirCiudad}
                    disabled={!estadoId || cargandoCiudades}
                    error={Boolean(errors.residenceCity) && Boolean(estadoId)}
                    canDelete={false}
                    placeholder={
                      !estadoId
                        ? 'Primero elige un estado'
                        : cargandoCiudades
                          ? 'Cargando ciudades...'
                          : 'Selecciona una ciudad'
                    }
                    placeholderDeBusqueda="Buscar ciudad..."
                    opciones={ciudades.map((c) => ({ value: String(c.id), label: c.name }))}
                  />
                ) : (
                <input
                  type="text"
                  id="residenceCity"
                  name="residenceCity"
                  maxLength={MAX_CIUDAD}
                  value={formData.residenceCity}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                    errors.residenceCity ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="Ej. Monterrey, Nuevo León"
                />
                )}
                {errors.residenceCity && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.residenceCity}
                  </p>
                )}
              </div>

              {/* Sueldo deseado */}
              <div>
                <label htmlFor="salaryExpectation" className="block text-sm font-medium text-gray-700 mb-2">
                  Sueldo Mensual Deseado
                </label>
                <input
                  type="number"
                  id="salaryExpectation"
                  name="salaryExpectation"
                  min="0"
                  step="0.01"
                  value={formData.salaryExpectation ?? ''}
                  onChange={handleSalaryChange}
                  onInvalid={e => traduceElAvisoDelSueldo(e.currentTarget)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                    errors.salaryExpectation ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="Ej. 22000"
                />
                <p className="mt-1 text-xs text-gray-500">Opcional. Bruto mensual en pesos.</p>
                {errors.salaryExpectation && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.salaryExpectation}
                  </p>
                )}
              </div>
            </div>

            {/* Experience Description */}
            <div>
              <label htmlFor="experienceDescription" className="block text-sm font-medium text-gray-700 mb-2">
                Descripción de Experiencia *
              </label>
              <textarea
                id="experienceDescription"
                name="experienceDescription"
                rows={4}
                value={formData.experienceDescription}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors resize-none ${
                  errors.experienceDescription ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
                placeholder="Describe brevemente tu experiencia laboral, habilidades y logros relevantes..."
              />
              {errors.experienceDescription && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.experienceDescription}
                </p>
              )}
            </div>

            {/*
              ¿Cómo te enteraste de esta vacante? (RH-06)

              Solo se pinta si el catálogo público trajo valores: si la carga
              falla, la postulación sigue sin este campo en vez de trabarse.
            */}
            {origenes.length > 0 && (
              <div>
                <label htmlFor="sourceId" className="block text-sm font-medium text-gray-700 mb-2">
                  ¿Cómo te enteraste de esta vacante?
                </label>
                <SelectBuscable
                  variante="formulario"
                  id="sourceId"
                  name="sourceId"
                  value={formData.sourceId != null ? String(formData.sourceId) : ''}
                  onChange={(valor) =>
                    setFormData((previos) => ({
                      ...previos,
                      sourceId: valor ? Number(valor) : undefined,
                      // Al cambiar de opción el nombre deja de aplicar. Si se
                      // quedara, viajaría con una opción que no lo pide.
                      referredByText: '',
                    }))
                  }
                  /* Vacío y neutro: «Prefiero no decirlo» se leía como una
                     respuesta ya dada, cuando nadie la había elegido. El
                     campo es opcional, así que dejarlo así manda lo mismo,
                     pero sin poner palabras en boca de quien se postula. */
                  placeholder="Selecciona una opción"
                  placeholderDeBusqueda="Buscar opción..."
                  opciones={origenes.map((origen) => ({
                    value: String(origen.id),
                    label: origen.name,
                  }))}
                />
              </div>
            )}

            {pideRecomendador && (
              <div>
                <label htmlFor="referredByText" className="block text-sm font-medium text-gray-700 mb-2">
                  ¿Quién te recomendó? *
                </label>
                <input
                  type="text"
                  id="referredByText"
                  name="referredByText"
                  maxLength={160}
                  value={formData.referredByText ?? ''}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                    errors.referredByText ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="Nombre de la persona que te recomendó"
                />
                {errors.referredByText && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.referredByText}
                  </p>
                )}
              </div>
            )}

            {/* CV Upload */}
            <div>
              <label htmlFor="cv" className="block text-sm font-medium text-gray-700 mb-2">
                Curriculum Vitae (PDF o Word){jobInfo?.tipoPerfil !== 'tecnico' ? ' *' : ' (Opcional)'}
              </label>
              
              {!cvFileName ? (
                <div className="relative">
                  <input
                    type="file"
                    id="cv"
                    name="cv"
                    accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={handleFileChange}
                    disabled={isUploadingCV}
                    className="hidden"
                  />
                  <label
                    htmlFor="cv"
                    className={`w-full flex items-center justify-center px-4 py-6 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                      isUploadingCV
                        ? 'border-blue-300 bg-blue-50'
                        : errors.cv
                        ? 'border-red-300 bg-red-50 hover:border-red-400'
                        : 'border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50'
                    }`}
                  >
                    <div className="text-center">
                      {isUploadingCV ? (
                        <>
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                          <p className="text-sm text-blue-600 font-medium">
                            Subiendo archivo...
                          </p>
                        </>
                      ) : (
                        <>
                          <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-sm text-gray-600 font-medium">
                            Haz clic para seleccionar tu CV
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            PDF o Word (máx. 5MB)
                          </p>
                        </>
                      )}
                    </div>
                  </label>
                </div>
              ) : (
                <div className="flex items-center justify-between p-4 border-2 border-green-300 bg-green-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <FileText className="w-8 h-8 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{cvFileName}</p>
                      <p className="text-xs text-green-600">✓ Archivo cargado exitosamente</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleRemoveCV}
                    className="flex-shrink-0 p-1 rounded-full text-gray-400 hover:text-red-600 hover:bg-red-100 transition-colors"
                    title="Eliminar archivo"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              )}
              
              {errors.cv && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.cv}
                </p>
              )}
            </div>

            {/* Privacy Consent */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-start">
                <Checkbox
                  id="privacyConsent"
                  name="privacyConsent"
                  checked={formData.privacyConsent}
                  onChange={handleCheckboxChange} className="mt-1"
                />
                <label htmlFor="privacyConsent" className="ml-3 text-sm text-gray-700">
                  <span className="font-medium">Consentimiento de Privacidad *</span>
                  <br />
                  He leído y acepto el
                  <a
                    href={RUTA_AVISO_INTEGRAL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline mx-1"
                  >
                    Aviso de Privacidad
                  </a>
                  de SLYRSA. Autorizo el tratamiento de mis datos personales para fines de
                  reclutamiento, selección y, en su caso, futuras oportunidades laborales.
                </label>
              </div>
              {errors.privacyConsent && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.privacyConsent}
                </p>
              )}
            </div>

            {/* Submit Button */}
            <div className="pt-6">
              <button
                type="submit"
                disabled={isSubmitting}
                className={estiloDeBoton('primario', 'w-full')}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Enviando...
                  </span>
                ) : (
                  'Enviar Registro'
                )}
              </button>
            </div>

            {/* Error Message */}
            {submitMessage && !isSubmitted && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                  <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                  <p className="text-sm text-red-700">{submitMessage}</p>
                </div>
              </div>
            )}
          </form>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-gray-500">
          <p className="text-sm">
            ¿Tienes dudas? Contáctanos en{' '}
            <a href="mailto:rh@slyrsa.com" className="text-blue-600 hover:underline">
              rh@slyrsa.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CandidateRegistrationForm;
